/* eslint-disable sonarjs/no-duplicate-string */
import { cloneDeep } from 'lodash'
import { BasketHelper } from '@/helpers/Basket'
import store from '@/store'
import { ProductUtil } from '@/utils/Product'

import {
  SALES_PARTNER_TYPE_PRIVATE_INSURER,
  SALES_PARTNER_TYPE_BROKER,
  SALES_PARTNER_TYPE_MOBILE,
  PRODUCT_STATE,
} from '@/config/constants'

export const ProductHelper = {
  ...ProductUtil,

  addProductDoctorToBasket(personId, productId, doctor, basket = null) {
    const updatedBasket = mutateProductsInBasket(personId, productId, { basket })
    const product = updatedBasket.products.products[productId]
    product.doctor = doctor
    return BasketHelper.updateBasketForDoctorPerson(personId, updatedBasket, doctor)
  },

  /**
   * @param {String} personId
   * @param {String} productId
   * @param {String} mode - REPLACE|ADD
   * @param {Object} options
   */
  addProductToBasket(personId, productId, options = {}) {
    const mode = options.mode ?? 'REPLACE'
    let doctor = options.doctor ?? null
    const basket = options.basket ?? null

    const product = basket
      ? basket.persons.find(p => p.personId === personId).products.products[productId]
      : store.getters['basket/getProduct'](personId, productId)

    if (!doctor) {
      doctor = product?.doctor
    }

    if (doctor) {
      return ProductHelper.addProductDoctorToBasket(personId, productId, doctor, basket)
    } else {
      const pBasket = mutateProductsInBasket(personId, productId, { basket, mode })
      return BasketHelper.updateBasketForPerson(personId, pBasket, true)
    }
  },

  state(partnerNumber, productId) {
    if (!partnerNumber) {
      return PRODUCT_STATE.EDITABLE
    }
    const isExistingCustomer = store.state.basket.existingCustomer
    if (!isExistingCustomer) return PRODUCT_STATE.EDITABLE

    const personId = store.getters['basket/getPersonByPartnerNumber'](partnerNumber).personId
    const basketProduct = store.getters['basket/getSelectedProductsOfPerson'](personId).find(p => p.id === productId)

    // Channel switch with newly added person, therefore existing customer not in contract-persons array
    const contractPerson = store.getters['contract/getPerson'](partnerNumber)
    if (!contractPerson) return PRODUCT_STATE.EDITABLE

    const contractProducts = store.getters['contract/getSelectedProductsOfPerson'](partnerNumber)
    const contractProductById = contractProducts.find(p => p.id === productId)

    const isProductWithinGroupAlreadySelected = () => {
      if (contractProductById && contractProductById.selected) return true

      const replaceableProducts = ProductHelper.getReplaceableProducts(productId)
      return replaceableProducts.find(
        productId => store.getters['contract/getProduct'](partnerNumber, productId)?.selected
      )
    }

    if (basketProduct?.selected && !isProductWithinGroupAlreadySelected()) {
      return PRODUCT_STATE.EDITABLE
    }

    // TODO: is second condition necessary?
    if (basketProduct?.selected && isProductWithinGroupAlreadySelected()) {
      if (basketProduct.price.duration && basketProduct.price.duration !== '1') {
        return PRODUCT_STATE.READONLY
      } else {
        return PRODUCT_STATE.NOT_DELETABLE
      }
    }
    return PRODUCT_STATE.EDITABLE
  },

  isUpgrade(productId) {
    const products = store.state.products.products
    return !!Object.keys(products).find(_productId => products[_productId].upgrades.includes(productId))
  },

  removeProductFromBasket(personId, productId) {
    return BasketHelper.updateBasketForPerson(
      personId,
      mutateProductsInBasket(personId, productId, { mode: 'REMOVE' }),
      true
    )
  },

  removeProductsFromBasket(personId, products) {
    const basket = cloneDeep(store.state.basket)
    const person = basket.persons.find(p => p.personId === personId)

    products.forEach(productId => {
      person.products.products[productId].selected = false
    })

    return Promise.resolve(basket)
  },

  selectProductOption(personId, productId, option, basket = null) {
    return new Promise(resolve => {
      const pBasket = basket
        ? basket.persons.find(p => p.personId === personId)
        : store.getters['basket/getPerson'](personId)
      const product = pBasket.products.products[productId]

      product.prices = product.prices.map(item => {
        if (item.value === option.value && item.duration === option.duration) {
          item.selected = option.selected
        } else {
          item.selected = false
        }

        if (option.toggle !== undefined) {
          item.toggle = option.toggle
        }
        return item
      })

      return BasketHelper.updateBasketForPerson(personId, pBasket).then(basket => resolve(basket))
    })
  },

  selectProductOptionForCategory(personId, categoryId, option) {
    const data = {
      products: [],
      mutations: [],
    }

    store.getters['basket/getGroupsFromCategory'](personId, categoryId)
      .reduce((acc, val) => acc.concat(val.products), [])
      .forEach(productId => {
        data.products.push(productId)
        data.mutations.push(ProductHelper.selectProductOption(personId, productId, option))
      })

    return Promise.all(data.mutations).then(fragments => {
      const products = fragments.reduce((acc, value, index) => {
        const productId = data.products[index]
        acc[productId] = value.persons.find(p => p.personId === personId).products.products[productId]
        return acc
      }, {})

      const basket = fragments[0]
      const personIndex = basket.persons.findIndex(p => p.personId === personId)

      basket.persons[personIndex].products.products = Object.assign(
        {},
        basket.persons[personIndex].products.products,
        products
      )

      return Promise.resolve(basket)
    })
  },

  transferProductToPerson(sourceId, targetId, productId, options = {}) {
    const basket = options.basket ?? null
    const mode = options.mode ?? 'REPLACE'
    const doctor = options.doctor ?? null
    const sBasket = basket
      ? basket.persons.find(p => p.personId === sourceId)
      : store.getters['basket/getPerson'](sourceId)
    const product = sBasket.products.products[productId]
    const option = product.prices.find(p => p.selected)

    return ProductHelper.addProductToBasket(targetId, productId, { basket, doctor, mode }).then(updatedBasket =>
      ProductHelper.selectProductOption(targetId, productId, option, updatedBasket)
    )
  },

  hasDuration(product) {
    const isDurationVisibleForPartners = [
      SALES_PARTNER_TYPE_PRIVATE_INSURER,
      SALES_PARTNER_TYPE_BROKER,
      SALES_PARTNER_TYPE_MOBILE,
    ].includes(store.getters['content/getPartner']()?.salesPartnerType)

    // eslint-disable-next-line no-prototype-builtins
    const hasProp = product.prices.find(p => p.selected)?.hasOwnProperty('duration')

    return (isDurationVisibleForPartners || product.id === 'SBVB01Produkt') && hasProp
  },

  hasOptions(product) {
    return this.hasAccident(product) || this.hasFranchise(product) || this.hasDuration(product)
  },

  optionTitles($te, $t, productId, groupId, categoryId) {
    const data = { franchise: {}, accident: {} }

    if ($te(`content.categories.${categoryId}.accident`)) {
      data.accident = $t(`content.categories.${categoryId}.accident`)
    } else if ($te(`content.groups.${groupId}.accident`)) {
      data.accident = $t(`content.groups.${groupId}.accident`)
    } else if ($te(`content.products.${productId}.accident`)) {
      data.accident = $t(`content.products.${productId}.accident`)
    }

    if ($te(`content.categories.${categoryId}.duration`)) {
      data.duration = $t(`content.categories.${categoryId}.duration`)
    } else if ($te(`content.groups.${groupId}.duration`)) {
      data.duration = $t(`content.groups.${groupId}.duration`)
    } else if ($te(`content.products.${productId}.duration`)) {
      data.duration = $t(`content.products.${productId}.duration`)
    }

    if ($te(`content.categories.${categoryId}.franchise`)) {
      data.franchise = $t(`content.categories.${categoryId}.franchise`)
    } else if ($te(`content.groups.${groupId}.franchise`)) {
      data.franchise = $t(`content.groups.${groupId}.franchise`)
    } else if ($te(`content.products.${productId}.franchise`)) {
      data.franchise = $t(`content.products.${productId}.franchise`)
    }

    return data
  },

  pricePrefix(product, $t) {
    return product?.showDiscountGroup && !product.selected ? $t('product.asof') : ''
  },

  productPricing(getProductPrice, personId, productId) {
    // Gets the product by productId from person with personId and return its price object, which is the same as
    // we already have in the product object within the component.
    return getProductPrice(personId, productId) || { price: 0 }
  },

  showPrice(product, productPricing) {
    return product.valid || product.selected || productPricing.price > 0
  },

  composeOptions(product, categoryId, optionTitles, $t) {
    const price = product.prices.find(p => p.selected)

    const options = []
    if (ProductHelper.hasAccident(product, categoryId)) {
      options.push($t(`product.accident.${price.toggle ? 'on' : 'off'}`))
    }

    if (ProductHelper.hasFranchise(product)) {
      const franchise = `${optionTitles.franchise.title} ${ProductHelper.formatCurrency(price.value, null, 0)} `
      options.push(franchise)
    }

    if (ProductHelper.hasDuration(product) && optionTitles.duration) {
      switch (price.duration.toString()) {
        case '1':
          options.push($t('product.contractDuration.single', { year: price.duration }))
          break

        case '3':
        case '5':
          options.push($t('product.contractDuration.multiple', { years: price.duration }))
          break

        case 'F':
          options.push($t('product.contractDuration.extended'))
          break
      }
    }

    return options.join(', ')
  },

  removeProduct(personId, productId) {
    return ProductHelper.removeProductFromBasket(personId, productId).then(basket =>
      BasketHelper.newUpdateBasket(basket)
    )
  },

  getReplaceableProducts(productId) {
    let replaceableProducts = []
    const category = store.getters['products/getCategoryFromProduct'](productId)
    const group = store.getters['products/getGroupFromProduct'](productId)

    const reducer = (acc, value) => {
      acc.push(value.id)
      return acc
    }

    if (category?.exclusive) {
      replaceableProducts = store.getters['products/getProductsFromCategory'](category.id).reduce(reducer, [])
    } else if (group?.exclusive) {
      replaceableProducts = store.getters['products/getProductsFromGroup'](group.id).reduce(reducer, [])
    }

    return replaceableProducts
  },

  willReplace(personId, productId) {
    const replaceableProducts = ProductHelper.getReplaceableProducts(productId)
    return replaceableProducts.find(productId => store.getters['basket/getProduct'](personId, productId)?.selected)
  },
}

/**
 * mutate a product in the basket (add, replace, remove)
 * @param  {String} personId
 * @param  {String} productId
 * @param  {String} mode      (ADD|REMOVE|REPLACE)
 * @param  {Basket|Boolean} basket
 * @return {Object} // returns the whole person from the basket
 */
function mutateProductsInBasket(personId, productId, options = {}) {
  const mode = options.mode ?? 'REPLACE'
  const basket = options.basket ?? null

  const pBasket = basket
    ? basket.persons.find(p => p.personId === personId)
    : store.getters['basket/getPerson'](personId)
  const products = pBasket.products.products

  const productKeys = Object.keys(products)
  const tmpBasket = {}

  switch (mode) {
    case 'ADD':
      productKeys.forEach(product => {
        tmpBasket[product] = Object.assign(products[product], {
          selected: productId === product ? true : products[product].selected,
        })
      })
      break

    case 'REMOVE':
      productKeys.forEach(product => {
        tmpBasket[product] = Object.assign(products[product], {
          selected: productId === product ? false : products[product].selected,
        })
      })
      break

    /**
     * replace existing selected products with the new one (apply rules)
     */
    case 'REPLACE':
      replaceProductInBasket(productId, tmpBasket, products, productKeys)
      break
  }

  pBasket.products.products = tmpBasket

  return pBasket
}

function replaceProductInBasket(productId, tmpBasket, products, productKeys) {
  let productsToReplace = []
  const categoryId = store.getters['products/getCategoryIdFromProduct'](productId)
  const category = store.state.products.categories[categoryId]
  const groupId = store.getters['products/getGroupIdFromProduct'](productId)
  const group = store.state.products.groups[groupId]

  const reducer = (acc, value) => {
    acc.push(value.id)
    return acc
  }

  if (category?.exclusive) {
    productsToReplace = store.getters['products/getProductsFromCategory'](categoryId).reduce(reducer, [])
  } else if (group?.exclusive) {
    productsToReplace = store.getters['products/getProductsFromGroup'](groupId).reduce(reducer, [])
  }

  productKeys.forEach(product => {
    if (product === productId) {
      tmpBasket[product] = Object.assign({}, products[product], {
        selected: true,
      })
    } else if (productsToReplace.includes(product)) {
      tmpBasket[product] = Object.assign({}, products[product], {
        selected: false,
      })
    } else {
      tmpBasket[product] = products[product]
    }

    return tmpBasket[product]
  })
}